import type {
  InitiatePaymentInput,
  InitiatePaymentResult,
  PaymentAdapter,
  PaymentStatusResult,
} from '@/lib/payment/types';
import { PaymentAdapterError } from '@/lib/payment/types';

/**
 * StubAdapter — el adapter usado en MVP H5.
 *
 * NO hace llamadas de red. Simula los tiempos y respuestas que tendría
 * un gateway real (ePayco, Wompi, Stripe). El checkoutClient lo combina
 * con un POST al endpoint `taquilla/payment/webhook/stub` (admin-lead)
 * para que el server-side complete el flujo confirm_payment().
 *
 * Diseño:
 * - `initiate()` devuelve flow='stub_immediate' con un payload de webhook
 *   pre-armado. El checkoutClient lo envía al webhook stub.
 * - `getStatus()` simula latencias (200-500ms) y devuelve 'authorized'
 *   tras la "tarjeta válida" (cualquier número que pase Luhn).
 * - Las fixtures de éxito/fallo se controlan por el último dígito del
 *   "número de tarjeta fake" que el usuario tipea (terminado en 9 = falla).
 *   Esto permite testear el camino de error sin tocar configuración.
 */
export class StubAdapter implements PaymentAdapter {
  public readonly slug = 'stub';
  public readonly displayName = 'Stub Sandbox (MVP)';

  async initiate(input: InitiatePaymentInput): Promise<InitiatePaymentResult> {
    // Validación liviana — el adapter real lo haría server-side.
    if (input.amount.amount_cents <= 0) {
      throw new PaymentAdapterError('INVALID_AMOUNT', 'Amount must be positive', false);
    }
    if (!input.idempotencyKey || input.idempotencyKey.length < 8) {
      throw new PaymentAdapterError('INVALID_IDEMPOTENCY_KEY', 'Missing key', false);
    }

    // El external reference de un stub es deterministic por (orderId, key)
    // — el server puede verificar idempotency match.
    const externalReference = `STUB-${input.orderId.slice(0, 8)}-${input.idempotencyKey.slice(0, 8)}`;

    return {
      externalReference,
      flow: 'stub_immediate',
      stubWebhookPayload: {
        provider_slug: 'stub',
        external_reference: externalReference,
        order_id: input.orderId,
        amount_cents: input.amount.amount_cents,
        currency: input.amount.currency,
        // El último dígito del key fake controla el path:
        //   acaba en '9' -> failed
        //   acaba en '0' -> requires_action (3DS sim)
        //   resto       -> captured
        simulated_outcome: simulateOutcome(input.idempotencyKey),
        // El webhook stub server-side recibe esto, valida (none_stub signature),
        // y dispara confirm_payment() si simulated_outcome='captured'.
        signature: 'stub-no-signature',
      },
    };
  }

  async getStatus(externalReference: string): Promise<PaymentStatusResult> {
    // El stub no consulta nada — solo devuelve "authorized" tras 100ms.
    await new Promise((r) => setTimeout(r, 100));
    return {
      status: 'authorized',
      externalReference,
      userFacingMessage: 'Pago de prueba autorizado (stub).',
    };
  }
}

function simulateOutcome(idempotencyKey: string): 'captured' | 'failed' | 'requires_action' {
  const last = idempotencyKey.charAt(idempotencyKey.length - 1);
  if (last === '9') return 'failed';
  if (last === '0') return 'requires_action';
  return 'captured';
}

/** Singleton — el checkoutClient lo elige según VITE_PAYMENT_PROVIDER_SLUG. */
export const stubAdapter = new StubAdapter();
